import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { User } from "../../models/User";
import {
  fetchUsers as fetchUsersApi,
  fetchUserFilterOptions as fetchUserFilterOptionsApi,
  createUser as createUserApi,
  updateUser as updateUserApi,
  deleteUser as deleteUserApi,
  updateUserBalance as updateUserBalanceApi,
} from "../../services/apiConfig";
import type { UserFilters, BillingType } from "../../services/api";

interface UsersState {
  /** The current page only - the server no longer sends the whole table. */
  users: User[];
  /** Rows matching the filters, across every page. Drives the pager. */
  total: number;
  loading: "idle" | "pending" | "succeeded" | "failed";
  error: string | null;
  filterOptions: BillingType[];
  filterOptionsLoaded: boolean;
}

const initialState: UsersState = {
  users: [],
  total: 0,
  loading: "idle",
  error: null,
  filterOptions: [],
  filterOptionsLoaded: false,
};

export const fetchUsers = createAsyncThunk(
  "users/fetchUsers",
  async (filters?: UserFilters) => {
    const response = await fetchUsersApi(filters);
    return response;
  },
);

export const fetchUserFilterOptions = createAsyncThunk(
  "users/fetchUserFilterOptions",
  async () => {
    const response = await fetchUserFilterOptionsApi();
    return response;
  },
);

export const addUser = createAsyncThunk(
  "users/addUser",
  async (user: Omit<User, "id" | "created_at">) => {
    const response = await createUserApi(user);
    return response;
  },
);

export const editUser = createAsyncThunk(
  "users/editUser",
  async (user: User) => {
    console.log("userSlice editUser called with:", user);
    const response = await updateUserApi(user.id, user);
    console.log("userSlice editUser API response:", response);
    return response;
  },
);

export const removeUser = createAsyncThunk(
  "users/removeUser",
  async (id: number) => {
    await deleteUserApi(id);
    return id;
  },
);

export const updateBalance = createAsyncThunk(
  "users/updateBalance",
  async ({
    userId,
    balanceIndex,
    amount,
  }: {
    userId: number;
    balanceIndex: number;
    amount: number;
  }) => {
    const response = await updateUserBalanceApi(userId, balanceIndex, amount);
    return response;
  },
);

const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(fetchUserFilterOptions.fulfilled, (state, action) => {
        state.filterOptions = action.payload;
        state.filterOptionsLoaded = true;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = "succeeded";
        state.users = action.payload.data;
        state.total = action.payload.total;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.error.message ?? "Failed to fetch users";
        try {
          const msg = state.error ?? "Failed to fetch users";
          setTimeout(() => {
            window.dispatchEvent(
              new CustomEvent("app-toast", {
                detail: {
                  message: String(msg),
                  severity: "error",
                },
              }),
            );
          }, 0);
        } catch (e) {
          /* ignore */
        }
      })
      .addCase(addUser.fulfilled, (state, action) => {
        state.users.push(action.payload);
        try {
          setTimeout(() => {
            window.dispatchEvent(
              new CustomEvent("app-toast", {
                detail: { message: "User created", severity: "success" },
              }),
            );
          }, 0);
        } catch (e) {}
      })
      .addCase(addUser.rejected, (state, action) => {
        const err = action.error.message ?? "Failed to create user";
        try {
          setTimeout(() => {
            window.dispatchEvent(
              new CustomEvent("app-toast", {
                detail: { message: err, severity: "error" },
              }),
            );
          }, 0);
        } catch (e) {}
      })
      .addCase(editUser.fulfilled, (state, action) => {
        state.users = state.users.map((user) =>
          user.id === action.payload.id ? action.payload : user,
        );
        try {
          setTimeout(() => {
            window.dispatchEvent(
              new CustomEvent("app-toast", {
                detail: { message: "User updated", severity: "success" },
              }),
            );
          }, 0);
        } catch (e) {}
      })
      .addCase(editUser.rejected, (state, action) => {
        const err = action.error.message ?? "Failed to update user";
        try {
          setTimeout(() => {
            window.dispatchEvent(
              new CustomEvent("app-toast", {
                detail: { message: err, severity: "error" },
              }),
            );
          }, 0);
        } catch (e) {}
      })
      .addCase(updateBalance.fulfilled, (state, action) => {
        const index = state.users.findIndex(
          (user) => user.id === action.payload.id,
        );
        if (index !== -1) {
          state.users[index] = action.payload;
        }
        try {
          setTimeout(() => {
            window.dispatchEvent(
              new CustomEvent("app-toast", {
                detail: { message: "Balance updated", severity: "success" },
              }),
            );
          }, 0);
        } catch (e) {}
      })
      .addCase(updateBalance.rejected, (state, action) => {
        const err = action.error.message ?? "Failed to update balance";
        try {
          setTimeout(() => {
            window.dispatchEvent(
              new CustomEvent("app-toast", {
                detail: { message: err, severity: "error" },
              }),
            );
          }, 0);
        } catch (e) {}
      })
      .addCase(removeUser.fulfilled, (state, action) => {
        state.users = state.users.filter((user) => user.id !== action.payload);
        try {
          setTimeout(() => {
            window.dispatchEvent(
              new CustomEvent("app-toast", {
                detail: { message: "User removed", severity: "success" },
              }),
            );
          }, 0);
        } catch (e) {}
      })
      .addCase(removeUser.rejected, (state, action) => {
        const err = action.error.message ?? "Failed to remove user";
        try {
          setTimeout(() => {
            window.dispatchEvent(
              new CustomEvent("app-toast", {
                detail: { message: err, severity: "error" },
              }),
            );
          }, 0);
        } catch (e) {}
      });
  },
});

export default usersSlice.reducer;
